import type { ApiResult } from "./contracts";
import type { Role } from "./roles";

/**
 * Клиент центра уведомлений (E10). Сервер отдаёт ГОТОВЫЕ заголовок и текст — они собираются там
 * же, где формируются сигналы (одна формулировка на центр и на будущую почтовую рассылку фазы 2).
 * Клиент решает только, как это показать.
 *
 * Что показывает сервер, тоже определяет он: пользователь видит лишь сигналы, адресованные ЛЮБОЙ
 * из его ролей (Приложение Г ТЗ, объединение ролей — Р-13). Никакой фильтрации «по правам» на
 * клиенте нет и быть не должно.
 */

/** Вид сигнала — строка Приложения Г (значения совпадают с `NotificationKind` сервера). */
export type NotificationKind =
  | "due_soon"
  | "due_today"
  | "overdue"
  | "escalation"
  | "claim_needed"
  | "claim_expired"
  | "limitation_warn"
  /**
   * Девятая строка Приложения Г: черновик ИИ-разбора поступил на верификацию (E11). Единственный
   * вид, у которого нет обязательства, — договора ещё нет вовсе. Ссылка на экран верификации
   * появится вместе с самим экраном.
   */
  | "draft_pending"
  /**
   * Раскладку черновика подтвердил не юрист (E17): юрист узнаёт о новом договоре, чтобы проверить
   * его выборочно. Предмет — ДОГОВОР: черновик уже подтверждён и неизменяем.
   */
  | "draft_confirmed"
  /** Договор на контроле изменён не Инициатором (E17) — один сигнал на договор в день. */
  | "contract_changed";

/** Критичность — она же порядок показа (сервер отдаёт список уже отсортированным). */
export type NotificationSeverity = "escalation" | "overdue" | "legal" | "soon";

/** Порядок критичностей — он же порядок групп в панели (совпадает с сортировкой сервера). */
export const SEVERITY_ORDER: readonly NotificationSeverity[] = [
  "escalation",
  "overdue",
  "legal",
  "soon",
];

/** Оформление по критичности — токены docs/07 (правило 9: без «сырых» hex). */
export const SEVERITY_META: Record<
  NotificationSeverity,
  { readonly label: string; readonly dot: string; readonly stamp: string }
> = {
  escalation: {
    label: "Эскалация",
    dot: "bg-overdue-fg",
    stamp: "text-overdue-fg bg-overdue-bg",
  },
  overdue: { label: "Просрочка", dot: "bg-overdue-fg", stamp: "text-overdue-fg bg-overdue-bg" },
  legal: { label: "Правовой сигнал", dot: "bg-accent", stamp: "text-accent bg-control-bg" },
  soon: { label: "Срок близко", dot: "bg-soon-fg", stamp: "text-soon-fg bg-soon-bg" },
};

export interface NotificationItem {
  readonly id: string;
  readonly kind: NotificationKind;
  readonly severity: NotificationSeverity;
  /**
   * Роль-адресат: у совмещающего роли видно, по какой из них пришёл сигнал. NULL — сигнал
   * адресован работнику ЛИЧНО (E17), и тогда вместо роли показывается пометка «лично Вам».
   */
  readonly role: Role | null;
  /**
   * Сигнал пришёл лично, а не по роли (E17): Инициатору договора. Отдельным признаком, а не
   * выводом из `role === null`: смысл «это на Вас, а не на отдел» пользователю важнее того, каким
   * полем он выражен, и в разметке это видно без пояснений.
   */
  readonly personal: boolean;
  /** Обязательство сигнала; NULL у `draft_pending` — у черновика обязательств ещё нет. */
  readonly obligationId: string | null;
  /** Черновик на верификации; NULL у остальных видов. */
  readonly draftId: string | null;
  /** Договор обязательства; NULL — объект уведомления недоступен, ссылки не будет. */
  readonly contractId: string | null;
  readonly claimId: string | null;
  /** День формирования `ГГГГ-ММ-ДД`. */
  readonly day: string;
  readonly createdAt: string;
  readonly read: boolean;
  /**
   * Сигнал больше не отражает действительность: обязательство исполнено, снято или ушло из
   * просрочки после сторнирования. Из счётчика такие уходят, но из списка не исчезают (Р-33).
   */
  readonly stale: boolean;
  readonly title: string;
  readonly text: string;
}

export interface NotificationCenter {
  readonly items: NotificationItem[];
  /** Непрочитанные АКТУАЛЬНЫЕ — их и показывает счётчик у колокольчика. */
  readonly unread: number;
  /**
   * Администратор включил приглушение сигналов по аффилированным (В18/Р-35). Показывается
   * отдельной строкой: юрист настроек не видит, и «тихое» приглушение обязан заметить здесь.
   */
  readonly affiliatedMuted: boolean;
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: body?.message ?? "Недостаточно прав." };
  }
  return {
    ok: false,
    reason: "network",
    message: body?.message ?? "Не удалось загрузить уведомления.",
  };
}

export async function fetchNotifications(): Promise<ApiResult<NotificationCenter>> {
  let res: Response;
  try {
    res = await fetch("/api/notifications", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as Partial<NotificationCenter> | null;
  if (!body || !Array.isArray(body.items)) {
    // Поломку контракта НЕ выдаём за пустой центр: «уведомлений нет» — это утверждение, и в
    // системе контроля сроков ложное «нет» опаснее честного «не смог загрузить».
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return {
    ok: true,
    value: {
      items: body.items,
      unread: body.unread ?? 0,
      affiliatedMuted: body.affiliatedMuted ?? false,
    },
  };
}

async function postRead(url: string, input?: unknown): Promise<ApiResult<number>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: input === undefined ? { Accept: "application/json" } : JSON_HEADERS,
      credentials: "same-origin",
      ...(input === undefined ? {} : { body: JSON.stringify(input) }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { marked?: number } | null;
  return { ok: true, value: body?.marked ?? 0 };
}

export function markNotificationsRead(ids: readonly string[]): Promise<ApiResult<number>> {
  return postRead("/api/notifications/read", { ids });
}

export function markAllNotificationsRead(): Promise<ApiResult<number>> {
  return postRead("/api/notifications/read-all");
}

/**
 * Куда ведёт уведомление.
 *
 * ЧЕРНОВИК НА ВЕРИФИКАЦИЮ — НА КАРТОЧКУ ЭТОГО ЧЕРНОВИКА (постановка 16.08.2026, адрес уточнён
 * D-24). Сигнал оставался строкой, по которой некуда нажать: юрист читал «разбор договора готов»
 * и шёл искать раздел «Верификация» руками, а черновиков в очереди бывает несколько — то есть
 * искал ещё и нужный.
 *
 * Адрес теперь СОБСТВЕННЫЙ у каждого черновика — `/verification/:id`, тот же, что открывает
 * строка списка. Прежний `/verify?draft=` работал, пока экран верификации был один на всю
 * очередь; с появлением списка (D-24) параметр запроса стал бы вторым способом сказать то же
 * самое, а два способа адресовать один объект рано или поздно расходятся.
 *
 * ОСТАЛЬНЫЕ СИГНАЛЫ — В КАРТОЧКУ ДОГОВОРА, к которой относится обязательство. Отдельного экрана
 * обязательства нет — они живут в карточке договора (E7), поэтому ссылка ведёт туда, но с
 * УКАЗАНИЕМ строки (`?obl=`): карточка её подсвечивает и подкручивает в поле зрения.
 *
 * ДОГОВОР БЕЗ ОБЯЗАТЕЛЬСТВА — ТОЖЕ ССЫЛКА. Раньше требовалось И то, И другое, и сигнал, у
 * которого известен только договор, никуда не вёл вовсе. Открыть карточку договора — заведомо
 * лучше, чем не открыть ничего.
 *
 * `null` — вести действительно некуда: объекта нет (договор удалён).
 */
export function notificationHref(item: NotificationItem): string | null {
  // «Раскладка подтверждена» знает и договор, и разбор — но ведёт В ДОГОВОР: там уже
  // подтверждённые обязательства, то есть предмет работы юриста. На сам разбор ведёт ВТОРАЯ
  // ссылка (`notificationDraftHref`), и порядок этих двух проверок — и есть это решение.
  if (item.kind === "draft_confirmed") {
    return item.contractId === null ? null : `/contracts/${item.contractId}`;
  }
  if (item.draftId !== null) return `/verification/${encodeURIComponent(item.draftId)}`;
  if (item.contractId === null) return null;
  if (item.obligationId === null) return `/contracts/${item.contractId}`;
  return `/contracts/${item.contractId}?obl=${encodeURIComponent(item.obligationId)}`;
}

/**
 * ВТОРАЯ ССЫЛКА — на сам разбор (решение владельца 17.09.2026). Есть только у сигнала
 * «Раскладка подтверждена»: юристу, проверяющему выборочно, нужно видеть не только то, что встало
 * на контроль, но и то, что предлагала модель и что из этого поправил Инициатор. У остальных
 * видов второго объекта нет, и `null` здесь означает «второй ссылки не показываем».
 */
export function notificationDraftHref(item: NotificationItem): string | null {
  if (item.kind !== "draft_confirmed" || item.draftId === null) return null;
  return `/verification/${encodeURIComponent(item.draftId)}`;
}
